import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle } from "lucide-react";
import { SEO } from "@/components/SEO";
import { RevealText } from "@/components/shared/RevealText";
import { SectionReveal } from "@/components/shared/SectionReveal";
import apiClient from "@/lib/api";

const gridVariants = { visible: { transition: { staggerChildren: 0.08 } } };
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

export default function Blog() {
  const { t } = useTranslation();
  const { lang = "fr" } = useParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const videos = t("blog.videos", { returnObjects: true });

  useEffect(() => {
    setLoading(true);
    apiClient
      .get(`/blog?lang=${lang}`)
      .then((res) => setArticles(Array.isArray(res.data) ? res.data : []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [lang]);

  return (
    <div data-testid="blog-page">
      <SEO title={`${t("blog.hero_title")} — Fastority`} description={t("blog.hero_subtitle")} path="blog" />

      <section className="bg-deepblue text-white py-24 sm:py-28 grain-overlay relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 relative">
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            <RevealText lines={[t("blog.hero_title")]} />
          </h1>
          <p className="mt-6 text-lg text-white/70">{t("blog.hero_subtitle")}</p>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-white" data-testid="blog-videos-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <SectionReveal className="mb-10">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate900">{t("blog.videos_title")}</h2>
            <p className="mt-2 text-slate-600">{t("blog.videos_subtitle")}</p>
          </SectionReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {videos.map((v, i) => (
              <SectionReveal key={v.title} delay={i * 0.08}>
                <div data-testid={`blog-video-${i}`} className="bg-offwhite rounded-2xl border border-gray-100 overflow-hidden group">
                  {v.youtube_id ? (
                    <div className="aspect-video">
                      <iframe
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${v.youtube_id}`}
                        title={v.title}
                        frameBorder="0"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        data-testid={`blog-video-iframe-${i}`}
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-slate900 flex items-center justify-center">
                      <PlayCircle className="text-white/70 group-hover:text-coral group-hover:scale-110 transition-all duration-300" size={36} />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-sm text-slate900 leading-snug">{v.title}</h3>
                    <p className="text-xs text-slate-600 mt-1 line-clamp-2">{v.desc}</p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-offwhite min-h-[40vh]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          {!loading && articles.length === 0 && (
            <p className="text-center text-slate-500" data-testid="blog-empty-state">{t("blog.empty")}</p>
          )}
          <motion.div
            key={loading ? "blog-grid-loading" : `blog-grid-${articles.length}`}
            initial="hidden"
            animate="visible"
            variants={gridVariants}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {articles.map((a) => (
              <motion.div key={a.id} variants={cardVariants}>
                <Link
                  to={`/${lang}/blog/${a.slug}`}
                  data-testid={`blog-card-${a.slug}`}
                  className="group block bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-[0_8px_30px_rgba(30,58,138,0.06)] h-full"
                >
                  <div className="aspect-[16/10] bg-deepblue/10 flex items-center justify-center overflow-hidden">
                    {a.cover_image ? (
                      <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <span className="font-heading font-extrabold text-3xl text-deepblue/20">Fastority</span>
                    )}
                  </div>
                  <div className="p-6">
                    <span className="text-xs uppercase tracking-wide font-bold text-coral">
                      {t(`blog.categories.${a.category}`, a.category)}
                    </span>
                    <h3 className="font-heading font-bold text-lg text-slate900 mt-2 mb-2 leading-snug">{a.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2">{a.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-deepblue mt-4">
                      {t("common.read_more")} <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
